<template>
  <div>
    <scorecards-empty
      v-if="!scorecardsData.data?.length && !scorecardsLoading"
      @new-template="emit('newTemplate')"
    />
    <loader v-else-if="scorecardsLoading" :is-loading="scorecardsLoading" />
    <template v-else>
      <div class="relative">
        <scorecard-list
          v-if="activeView === VIEW_TYPE_LIST"
          v-model:selected-ids="selectedIds"
          :data="scorecardsData.data || []"
          :loading="scorecardsLoading"
          :select="enableSelection"
        />
        <scorecards-tiles
          v-if="activeView === VIEW_TYPE_TILE"
          v-model:selected-ids="selectedIds"
          :data="scorecardsData.data"
          :select="enableSelection"
        />
        <lf-pagination
          :metadata="scorecardsData.meta"
          show-per-page
          @change-page="changePage"
          @change-items-per-page="changeItemsPerPage"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import debounce from "lodash/debounce";
import uniq from "lodash/uniq";

import { AnalyticsEvent } from "@/enums/analytics";
import { SortOption } from "@/enums/scorecards";
import { filterArrayFromNullish } from "@/helpers/common";
import {
  SCORECARDS_STATUSES,
  VIEW_TYPE_LIST,
  VIEW_TYPE_TILE,
} from "@/helpers/constants";
import {
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { useAuth } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";
import { useScorecards, useScorecardsBase } from "@/hooks/scorecards";
import { useActiveStage, useActiveWorkflowTemplate } from "@/hooks/workflow";
import { useAnalyticsStore } from "@/stores/analytics";

import ScorecardList from "@/components/scorecards/ScorecardList.vue";
import ScorecardsEmpty from "@/components/scorecards/ScorecardsEmpty.vue";
import ScorecardsTiles from "@/components/scorecards/ScorecardsTiles.vue";
import LfPagination from "@/components/ui/table/LfPagination.vue";

import type {
  IRunScorecardsPayload,
  IScorecard,
  IScorecardsViewParams,
} from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "updateParams", newParams: IScorecardsViewParams): void;
  (eventName: "newTemplate"): void;
}>();

const props = defineProps({
  activeView: {
    type: String,
    required: true,
  },
  params: {
    type: Object as PropType<IScorecardsViewParams>,
    required: true,
  },
  enableSelection: {
    type: Boolean,
    default: false,
  },
  showActionMenu: {
    type: Boolean,
    default: true,
  },
  statusesToShow: {
    type: Array as PropType<IScorecard["status"][]>,
  },
});
const route = useRoute();
const isEdit = route.name === "ScorecardsGroupEdit";
const { activeScorecardGroup } = useScorecards(isEdit);
const { scorecardsData, runScorecardIds } = useScorecardsBase();
const { dispatch, commit, getters } = useStore();
const { trackEvent } = useAnalyticsStore();
const { activeStage, isManagedByLendflow } = useActiveStage();
const { activeDeal } = useDealsBase();
const { activeTemplate } = useActiveWorkflowTemplate();
const { isLendflowUser } = useAuth();

const scorecardsLoading = ref(false);
const selectedIds = ref<string[]>([]);

const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder);
const pageIsView = inject(ScorecardsIsViewKey, true);

const isWorkflowLive = !pageIsView && !isWorkflowBuilder;

const scorecardHasBeenRun = computed<boolean>(
  () => getters["scorecards/scorecardHasBeenRun"]
);

const assignSelectedIds = () => {
  selectedIds.value =
    activeScorecardGroup.value?.score_cards?.map((sc) => sc.id) || [];
};

const updateParams = (newParams: Partial<IScorecardsViewParams>) => {
  const updatedParams = { ...props.params, ...newParams };
  emit("updateParams", updatedParams);
};

const changePage = (page: number) => {
  updateParams({ page });
};

const changeItemsPerPage = (per_page: number) => {
  updateParams({ per_page, page: 1 });
};

const handleRun = debounce(async (ids: string[]) => {
  if (!activeStage.value) {
    return;
  }

  const payload: {
    appId: string;
    data: IRunScorecardsPayload;
  } = {
    appId: activeDeal.value.id,
    data: { stage_id: activeStage.value.id },
  };

  payload.data = {
    ["score_card_ids"]: ids,
    stage_id: activeStage.value.id,
  };
  await dispatch("scorecards/runScorecards", payload);

  trackEvent(AnalyticsEvent.ScorecardRunTriggered, {
    scorecard_ids: ids,
    scorecard_count: ids.length,
  });
}, 1500);

const getScorecards = async () => {
  scorecardsLoading.value = true;
  const params = {
    ...props.params,
  };

  if (isWorkflowBuilder && isLendflowUser) {
    params.client_id = activeTemplate.value.client?.id || "";
    if (isManagedByLendflow.value) {
      params.include_lendflow = 1;
    }
  }

  if (isWorkflowBuilder) {
    params.statuses = [SCORECARDS_STATUSES.FINISHED];
  }

  if (activeStage.value) {
    params.sort_by = SortOption.LATEST_RUN_FIRST;
  }

  if (props.statusesToShow !== undefined) {
    const statusesToShow = filterArrayFromNullish(props.statusesToShow);
    params.statuses = uniq([...(params.statuses || []), ...statusesToShow]);
  }

  if (isWorkflowLive) {
    params.workflow_stage_id = activeStage.value?.id;
  }

  await dispatch("scorecards/getAll", params);
  scorecardsLoading.value = false;
};

watch(
  () => props.params.search,
  () => {
    updateParams({ page: 1 });
  }
);

watch(() => props.params, getScorecards);

watch(
  activeScorecardGroup,
  () => {
    if (!isEdit) {
      return;
    }
    assignSelectedIds();
  },
  { immediate: true }
);

watch(runScorecardIds, (newValue, oldValue) => {
  if (newValue.length && newValue.length > oldValue.length) {
    handleRun(newValue);
  }
});

watch(scorecardHasBeenRun, async (newValue, oldValue) => {
  if (newValue && !oldValue) {
    await getScorecards();
    commit("scorecards/setScorecardHasBeenRun", false);
  }
});

onMounted(() => {
  getScorecards();

  if (isWorkflowBuilder) {
    selectedIds.value = activeStage.value?.cards || [];
  }
});

onBeforeUnmount(() => {
  commit("scorecards/clear");
});

defineExpose({
  selectedIds,
  scorecardsLoading,
});
</script>
